import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  FormText,
  Alert,
  Progress
} from "reactstrap";
import axios from "axios";
import { useSelector } from "react-redux";
import { ApplicationState } from "store";
import Filter from "./Filter/Filter";
import FieldMapper, { Option } from "./FieldMapper";
import config from "../../../config";
import { AudienceData } from "store/campaigns/types";

const { api } = config;
const BASE = api.DTS_API_CAMPAIGN;

interface StepAudienceProps {
  audience: AudienceData;
  onChange: (aud: AudienceData) => void;
  dataFields: { id: string; Header: string }[];
  audienceSnapshotMode: any;
  setAudienceSnapshotMode: (key: any) => void;
  t: (key: string) => string;
  setSelectedFile: (key: any) => void;
  SelectedFile: any;
}

const StepAudience: React.FC<StepAudienceProps> = ({
  audience,
  onChange,
  dataFields,
  audienceSnapshotMode,
  setAudienceSnapshotMode,
  setSelectedFile,
  SelectedFile,
  t
}) => {
  const token = useSelector((s: ApplicationState) => s.auth.token);
  const { type, columns, mappings = [], filter_groups } = audience;

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [localError, setLocalError] = useState<string | null>(null);

  // 1) Seed mappings for new columns (if mappings empty)
  useEffect(() => {
    if (columns?.length && mappings?.length === 0) {
      onChange({
        ...audience,
        mappings: columns.map(col => ({
          form_field: { value: col, label: col },
          crm_field: null // initially unmapped
        }))
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns?.join("|")]);

  // 2) File picker
  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalError(null);
    const f = e.target.files?.[0] ?? null;
    if (!f) return;
    onChange({ ...audience, file: f, document_key: "", columns: [], mappings: [] });
    uploadCsv(f);
    setSelectedFile(f);

  };

  // 3) Upload CSV → get headers
  async function uploadCsv(f: File) {
    setUploading(true);
    setUploadProgress(0);
    setLocalError(null);
    const form = new FormData();
    form.append("file", f);

    try {
      const resp = await axios.post<{ document_id: string; columns: string[] }>(
        `${BASE}/api/v1/imports/upload`,
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`
          },
          onUploadProgress: ev =>
            setUploadProgress(Math.round((ev.loaded / (ev.total || 1)) * 100))
        }
      );
      const { document_id: docId, columns: cols } = resp as any;
      onChange({
        ...audience,
        document_key: docId,
        columns: cols
      });
    } catch (err: any) {
      setLocalError(t("CSV upload failed. Please check your file and try again."));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }

  // 4) Mapping change
  const handleMappingChange = (idx: number, opt: Option | null) => {
    setLocalError(null);
    const next = mappings?.slice();
    next[idx] = { ...next[idx], crm_field: opt };
    onChange({ ...audience, mappings: next });
  };

  // 5) Filter change
  const handleFilterChange = (groups: any[]) => {
    setLocalError(null);
    onChange({ ...audience, filter_groups: groups });
  };

  // Build dropdown options
  const fieldOptions: Option[] = dataFields.map(f => ({
    value: f.id,
    label: f.Header
  }));

  // Premium: Only send mapped fields (not all columns)
  // Call this in parent when submitting!
  // const mappedFields = mappings.filter(m => m.crm_field).map(m => ({
  //   form_field: m.form_field,
  //   crm_field: m.crm_field
  // }));

  return (
    <>
      {/* Error Banner */}
      {localError && (
        <Alert color="danger" fade className="mb-3 rounded-3 shadow-sm d-flex align-items-center gap-2" toggle={() => setLocalError(null)}>
          <i className="ri-error-warning-line fs-4 me-2 text-danger" />
          <div>{localError}</div>
        </Alert>
      )}

      {/* ── STATIC vs DYNAMIC toggle ── */}
      <Row className="mb-3">
        <Col>
          <FormGroup check inline>
            <Input
              type="radio"
              name="audType"
              checked={type === "STATIC"}
              onChange={() => { setLocalError(null); onChange({ ...audience, type: "STATIC" }) }}
              disabled={uploading}
            />
            <Label check className="ms-2">{t("Static (CSV)")}</Label>
          </FormGroup>
          <FormGroup check inline>
            <Input
              type="radio"
              name="audType"
              checked={type === "DYNAMIC"}
              onChange={() => { setLocalError(null); onChange({ ...audience, type: "DYNAMIC" }) }}
              disabled={uploading}
            />
            <Label check className="ms-2">{t("Dynamic (Filters)")}</Label>
          </FormGroup>
          <FormText color="muted" className="mt-1">
            {type === "STATIC"
              ? t("Upload a CSV of recipient IDs, one per line. You only need to map fields you wish to use.")
              : t("Build condition groups to target your audience.")}
          </FormText>
        </Col>
      </Row>

      {/* ── STATIC CSV + mapping UI ── */}
      {type === "STATIC" && (
        <>
          <Row className="mb-4">
            <Col md={6}>
              <Input
                type="file"
                accept=".csv"
                onChange={pickFile}
                disabled={uploading}
              />
              {SelectedFile && (
                <div className="mt-2">
                  Selected File: <strong>{SelectedFile?.name}</strong>
                </div>
              )}

              {uploading && (
                <div className="mt-2">
                  <FormText className="mb-1">{t("Uploading")}: {uploadProgress}%</FormText>
                  <Progress color="primary" value={uploadProgress} className="rounded-3" style={{ height: 8 }} />
                </div>
              )}
              {!uploading && columns?.length > 0 && (
                <FormText className="mt-2">
                  <i className="ri-file-list-2-line me-1" style={{ opacity: 0.7 }} />
                  {t("Columns detected")}: <span className="fw-semibold">{columns.join(", ")}</span>
                </FormText>
              )}
            </Col>
          </Row>
          {columns?.length > 0 && (
            <FieldMapper
              title={t("Map your columns")}
              description={t("Map only the CSV columns you wish to import. Leave others blank.")}
              mapping={mappings}
              fieldOptions={fieldOptions}
              loading={uploading}
              onChange={handleMappingChange}
            />
          )}
        </>
      )}

      {/* ── DYNAMIC filters ── */}
      {type === "DYNAMIC" && (
        <>
          <Row className="mb-2">
            <Col>
              <FormGroup check>
                <Input
                  type="checkbox"
                  checked={audienceSnapshotMode === "STATIC"}
                  onChange={e => setAudienceSnapshotMode(e.target.checked ? "STATIC" : "DYNAMIC")}
                  disabled={uploading}
                />
                <Label check className="ms-2">
                  {t("Lock recipients now (snapshot)")}
                </Label>
                <FormText color="muted">
                  {audienceSnapshotMode === "STATIC"
                    ? t("Recipients matching the filter will be locked now. Later changes to students will not affect the campaign.")
                    : t("Recipients will be resolved at send time using the latest filter results.")}
                </FormText>
              </FormGroup>
            </Col>
          </Row>
          <Filter
            dataFields={dataFields}
            groups={filter_groups}
            updateGroups={handleFilterChange}
            t={t}
          />
        </>
      )}
    </>
  );
};

export default StepAudience;
